import { SxProps, Theme } from "@mui/material";
import t from "./ContractSearch.tokens";

/** Shared keyboard focus ring — the app-wide brand ring, never a bespoke colour. */
const focusVisible = {
  "&:focus-visible": {
    outline: `2px solid ${t.focusRing}`,
    outlineOffset: "2px",
  },
} as const;

/** Resets a `<button>` so a design surface can be a real button without the UA chrome. */
const buttonReset = {
  appearance: "none",
  font: "inherit",
  textAlign: "left",
  cursor: "pointer",
  ...focusVisible,
} as const;

const contractSearchStyles = {
  // -------------------------------------------------------------------- page

  /**
   * Same frame idea as Contacts: a column that fills exactly the viewport left
   * under the sticky TopNav (64px + its 1px hairline) and the layout's top
   * margin. The layout's 48px bottom padding is cancelled with a negative
   * margin at every breakpoint, so the composer sits at the bottom of the
   * screen and the page itself never gains a scrollbar.
   */
  page: {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    height: {
      xs: `calc(100vh - ${t.page.topNavHeight + t.page.topNavBorder}px)`,
      md: `calc(100vh - ${
        t.page.topNavHeight + t.page.topNavBorder + t.page.layoutMarginTop.md
      }px)`,
      lg: `calc(100vh - ${
        t.page.topNavHeight + t.page.topNavBorder + t.page.layoutMarginTop.lg
      }px)`,
    },
    mt: { xs: "-16px", sm: "-32px", md: 0 },
    mx: { xs: "-16px", sm: "-24px", md: 0 },
    mb: `-${t.page.layoutPaddingBottom}px`,
    pt: { xs: "16px", md: t.page.paddingTop },
    px: { xs: "16px", md: t.page.paddingX },
    pb: { xs: 0, md: t.page.paddingBottom },
  } as SxProps<Theme>,

  /**
   * The chat area under the header. Unlike the list pages there is no white
   * card: the thread sits straight on the page background with the composer
   * floating at the bottom, as the design draws it.
   */
  chatArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  } as SxProps<Theme>,

  /**
   * The middle band: thread (and the side viewer) scroll together inside it.
   * It scrolls (wheel, trackpad, touch, keyboard) but never draws a scrollbar --
   * the design keeps the column clean. Same recipe the field palette uses.
   */
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    pt: t.chatArea.paddingTop,
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
  } as SxProps<Theme>,

  columns: {
    display: "flex",
    alignItems: "flex-start",
    gap: t.layout.columnGap,
    width: "100%",
  } as SxProps<Theme>,

  threadColumn: {
    flex: 1,
    minWidth: 0,
    maxWidth: t.layout.threadMaxWidth,
    mx: "auto",
    display: "flex",
    flexDirection: "column",
  } as SxProps<Theme>,

  /** The composer's own centred column, same width as the thread. */
  composerColumn: {
    width: "100%",
    maxWidth: t.layout.threadMaxWidth,
    mx: "auto",
  } as SxProps<Theme>,

  // ------------------------------------------------------------------ header

  /** The Contacts header block: title + subtitle left, actions right. */
  header: {
    flexShrink: 0,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "flex-start", sm: "flex-end" },
    justifyContent: "space-between",
    gap: t.header.gap,
    mb: t.header.marginBottom,
  } as SxProps<Theme>,

  headerTitle: {
    m: 0,
    color: t.header.titleColor,
    fontSize: t.header.titleFontSize,
    fontWeight: t.header.titleFontWeight,
    letterSpacing: t.header.titleLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  headerSubtitle: {
    mt: t.header.subtitleMarginTop,
    color: t.header.subtitleColor,
    fontSize: t.header.subtitleFontSize,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** Phone stand-in: the app bar carries the screen name, the page keeps the one-line count. */
  phoneSubtitle: {
    m: 0,
    color: t.header.subtitleColor,
    fontSize: t.header.phoneSubtitleFontSize,
    lineHeight: "normal",
  } as SxProps<Theme>,

  headerCount: {
    color: t.header.countColor,
    fontWeight: t.header.countFontWeight,
  } as SxProps<Theme>,

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: t.header.gap,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** The indexed-count pill; sits beside the button and shares its height. */
  indexedChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: t.indexedChip.gap,
    height: t.indexedChip.height,
    px: t.indexedChip.paddingX,
    borderRadius: t.indexedChip.radius,
    bgcolor: t.indexedChip.bg,
    color: t.indexedChip.color,
    fontSize: t.indexedChip.fontSize,
    fontWeight: t.indexedChip.fontWeight,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "& svg": { color: t.indexedChip.iconColor },
  } as SxProps<Theme>,

  /** Outlined header action, styled like Contacts' "Add Contact". */
  secondaryButton: {
    "&.MuiButton-root": {
      display: "flex",
      alignItems: "center",
      gap: t.secondaryButton.gap,
      minWidth: 0,
      height: t.secondaryButton.height,
      padding: `0 ${t.secondaryButton.paddingX}`,
      background: t.secondaryButton.bg,
      border: `1px solid ${t.secondaryButton.border}`,
      borderRadius: t.secondaryButton.radius,
      color: t.secondaryButton.color,
      fontSize: t.secondaryButton.fontSize,
      fontWeight: t.secondaryButton.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        background: t.secondaryButton.hoverBg,
        border: `1px solid ${t.secondaryButton.border}`,
      },
    },
  } as SxProps<Theme>,

  // ------------------------------------------------------------- empty state

  emptyWrap: {
    textAlign: "center",
    pt: t.empty.paddingTop,
    pb: t.empty.paddingBottom,
    px: 1,
  } as SxProps<Theme>,

  emptyIconTile: {
    width: t.empty.iconTile,
    height: t.empty.iconTile,
    borderRadius: t.empty.iconTileRadius,
    bgcolor: t.empty.iconTileBg,
    color: t.empty.iconColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mx: "auto",
    mb: t.empty.iconMarginBottom,
  } as SxProps<Theme>,

  emptyTitle: {
    fontSize: t.empty.titleFontSize,
    fontWeight: 800,
    color: t.header.titleColor,
    lineHeight: 1.3,
    mb: t.empty.titleMarginBottom,
  } as SxProps<Theme>,

  emptyBody: {
    m: `0 auto ${t.empty.bodyMarginBottom}`,
    fontSize: t.empty.bodyFontSize,
    lineHeight: t.empty.bodyLineHeight,
    color: t.empty.bodyColor,
    maxWidth: t.empty.bodyMaxWidth,
  } as SxProps<Theme>,

  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: t.empty.chipGap,
    justifyContent: "center",
    maxWidth: t.empty.chipsMaxWidth,
    mx: "auto",
  } as SxProps<Theme>,

  chip: {
    ...buttonReset,
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    bgcolor: t.chip.bg,
    border: `1px solid ${t.chip.border}`,
    borderRadius: t.chip.radius,
    color: t.chip.color,
    fontSize: t.chip.fontSize,
    fontWeight: t.chip.fontWeight,
    px: t.chip.paddingX,
    py: t.chip.paddingY,
    lineHeight: 1.3,
    transition: "background-color .15s, border-color .15s",
    "&:hover": {
      bgcolor: t.chip.hoverBg,
      borderColor: t.chip.hoverBorder,
    },
    "&:disabled": { opacity: 0.55, cursor: "default" },
  } as SxProps<Theme>,

  chipArrow: {
    width: 13,
    height: 13,
    flexShrink: 0,
    color: t.chip.arrowColor,
  } as SxProps<Theme>,

  // ------------------------------------------------------------------ thread

  thread: {
    display: "flex",
    flexDirection: "column",
    gap: 2.75,
    pb: t.layout.composerClearance,
  } as SxProps<Theme>,

  userRow: {
    display: "flex",
    justifyContent: "flex-end",
  } as SxProps<Theme>,

  userBubble: {
    bgcolor: t.bubble.userBg,
    color: t.bubble.userColor,
    borderRadius: t.bubble.userRadius,
    fontSize: t.bubble.userFontSize,
    lineHeight: 1.55,
    px: "15px",
    py: "11px",
    maxWidth: t.bubble.userMaxWidth,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  } as SxProps<Theme>,

  aiRow: {
    display: "flex",
    gap: `${t.bubble.turnGap}px`,
  } as SxProps<Theme>,

  aiAvatar: {
    width: t.bubble.aiAvatarSize,
    height: t.bubble.aiAvatarSize,
    borderRadius: t.bubble.aiAvatarRadius,
    bgcolor: t.bubble.aiAvatarBg,
    color: t.bubble.aiAvatarColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  aiBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "13px",
  } as SxProps<Theme>,

  // ------------------------------------------------------------- answer card

  card: (refusal: boolean = false) =>
    ({
      bgcolor: t.card.bg,
      border: `1px solid ${refusal ? t.card.refusalBorder : t.card.border}`,
      borderRadius: t.card.radius,
      p: t.card.padding,
    }) as SxProps<Theme>,

  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    mb: 1,
    color: t.card.titleColor,
    fontSize: t.card.titleFontSize,
    fontWeight: 800,
  } as SxProps<Theme>,

  answerBody: {
    m: 0,
    color: t.card.bodyColor,
    fontSize: t.card.bodyFontSize,
    lineHeight: t.card.bodyLineHeight,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  } as SxProps<Theme>,

  /** A Markdown answer: paragraphs and per-contract headings stacked with a small gap. */
  answerBlocks: {
    display: "flex",
    flexDirection: "column",
    gap: t.card.blockGap,
  } as SxProps<Theme>,

  answerHeading: {
    m: 0,
    mt: t.card.headingMarginTop,
    color: t.card.titleColor,
    fontSize: t.card.bodyFontSize,
    fontWeight: 800,
    lineHeight: 1.4,
    "&:first-of-type": { mt: 0 },
  } as SxProps<Theme>,

  partialBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    fontSize: t.partialBadge.fontSize,
    fontWeight: 700,
    color: t.partialBadge.color,
    bgcolor: t.partialBadge.bg,
    border: `1px solid ${t.partialBadge.border}`,
    borderRadius: "999px",
    px: "11px",
    py: "5px",
    mb: "11px",
  } as SxProps<Theme>,

  partialNote: {
    mt: "13px",
    pt: "13px",
    borderTop: `1px dashed ${t.card.dividerColor}`,
    fontSize: t.card.noteFontSize,
    lineHeight: 1.65,
    color: t.card.noteColor,
  } as SxProps<Theme>,

  thinking: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    bgcolor: t.card.bg,
    border: `1px solid ${t.card.border}`,
    borderRadius: t.card.radius,
    px: "18px",
    py: "15px",
    width: "fit-content",
    "@keyframes csBlink": {
      "0%, 80%, 100%": { opacity: 0.25 },
      "40%": { opacity: 1 },
    },
  } as SxProps<Theme>,

  thinkingDot: (delay: number) =>
    ({
      width: t.thinking.dotSize,
      height: t.thinking.dotSize,
      borderRadius: "50%",
      bgcolor: t.thinking.dotColor,
      animation: `csBlink 1.1s ${delay}s infinite`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    }) as SxProps<Theme>,

  thinkingText: {
    fontSize: t.thinking.textFontSize,
    fontWeight: 600,
    color: t.thinking.textColor,
    ml: "4px",
  } as SxProps<Theme>,

  // -------------------------------------------------------- matched contracts

  matchedCard: {
    bgcolor: t.card.bg,
    border: `1px solid ${t.card.border}`,
    borderRadius: t.card.radius,
    overflow: "hidden",
  } as SxProps<Theme>,

  matchedHeading: {
    display: "flex",
    alignItems: "baseline",
    gap: "9px",
    px: "18px",
    py: "13px",
    borderBottom: `1px solid ${t.card.dividerColor}`,
    flexWrap: "wrap",
  } as SxProps<Theme>,

  matchedHeadingLabel: {
    fontSize: t.matched.headingFontSize,
    fontWeight: 800,
    letterSpacing: t.matched.headingLetterSpacing,
    color: t.matched.headingColor,
  } as SxProps<Theme>,

  matchedHeadingCount: {
    fontSize: t.matched.countFontSize,
    fontWeight: 700,
    color: t.matched.countColor,
  } as SxProps<Theme>,

  contractRow: {
    ...buttonReset,
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    p: t.matched.rowPadding,
    bgcolor: "transparent",
    border: 0,
    borderBottom: `1px solid ${t.matched.rowBorder}`,
    "&:hover": { bgcolor: t.matched.rowHoverBg },
    "&:focus-visible": {
      outline: `2px solid ${t.focusRing}`,
      outlineOffset: "-2px",
    },
  } as SxProps<Theme>,

  pdfTile: {
    width: t.matched.tileWidth,
    height: t.matched.tileHeight,
    borderRadius: "5px",
    bgcolor: t.matched.tileBg,
    border: `1px solid ${t.matched.tileBorder}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: t.matched.tileLabelFontSize,
    fontWeight: 800,
    color: t.matched.tileLabelColor,
  } as SxProps<Theme>,

  contractInfo: {
    flex: 1,
    minWidth: 0,
  } as SxProps<Theme>,

  contractFile: {
    fontSize: t.matched.fileFontSize,
    fontWeight: 700,
    color: t.matched.fileColor,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  contractMeta: {
    fontSize: t.matched.metaFontSize,
    fontWeight: 600,
    color: t.matched.metaColor,
    mt: "2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  contractTag: (used: boolean) =>
    ({
      flexShrink: 0,
      fontSize: t.matched.tagFontSize,
      fontWeight: 700,
      borderRadius: "999px",
      px: "9px",
      py: "3px",
      bgcolor: used ? t.matched.usedTagBg : t.matched.unusedTagBg,
      color: used ? t.matched.usedTagColor : t.matched.unusedTagColor,
      whiteSpace: "nowrap",
    }) as SxProps<Theme>,

  chevron: (open: boolean) =>
    ({
      width: 14,
      height: 14,
      flexShrink: 0,
      color: t.matched.chevronColor,
      transition: "transform .15s",
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
    }) as SxProps<Theme>,

  passagesWrap: {
    bgcolor: t.matched.passagesBg,
    borderBottom: `1px solid ${t.matched.rowBorder}`,
    px: "18px",
    pt: "13px",
    pb: "15px",
  } as SxProps<Theme>,

  passagesHeading: {
    fontSize: t.matched.passagesHeadingFontSize,
    fontWeight: 800,
    letterSpacing: ".08em",
    color: t.matched.passagesHeadingColor,
    mb: "9px",
  } as SxProps<Theme>,

  passagesList: {
    display: "flex",
    flexDirection: "column",
    gap: "9px",
    listStyle: "none",
    m: 0,
    p: 0,
  } as SxProps<Theme>,

  passage: (active: boolean) =>
    ({
      ...buttonReset,
      width: "100%",
      display: "block",
      bgcolor: active ? t.passage.activeBg : t.passage.bg,
      border: `1px solid ${active ? t.passage.activeBorder : t.passage.border}`,
      borderRadius: t.passage.radius,
      p: t.passage.padding,
      transition: "border-color .15s, background-color .15s",
      "&:hover": { borderColor: t.passage.hoverBorder },
    }) as SxProps<Theme>,

  passageHead: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: "6px",
    flexWrap: "wrap",
  } as SxProps<Theme>,

  passagePage: {
    fontSize: t.passage.pageFontSize,
    fontWeight: 800,
    color: t.passage.pageColor,
  } as SxProps<Theme>,

  passageDot: {
    width: 3,
    height: 3,
    borderRadius: "50%",
    bgcolor: t.matched.chevronColor,
  } as SxProps<Theme>,

  passageLines: {
    fontSize: t.passage.pageFontSize,
    fontWeight: 600,
    color: t.passage.linesColor,
  } as SxProps<Theme>,

  passageOpen: {
    ml: "auto",
    fontSize: t.passage.openFontSize,
    fontWeight: 700,
    color: t.passage.openColor,
  } as SxProps<Theme>,

  passageQuote: {
    fontSize: t.passage.quoteFontSize,
    lineHeight: t.passage.quoteLineHeight,
    color: t.passage.quoteColor,
    borderLeft: `3px solid ${t.passage.quoteRule}`,
    pl: "11px",
    overflowWrap: "anywhere",
  } as SxProps<Theme>,

  // --------------------------------------------------------------- follow-ups

  followUpsHeading: {
    fontSize: t.matched.headingFontSize,
    fontWeight: 800,
    letterSpacing: t.matched.headingLetterSpacing,
    color: t.matched.headingColor,
    mb: "9px",
  } as SxProps<Theme>,

  followUpsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
  } as SxProps<Theme>,

  // ------------------------------------------------------------------ actions

  actionsRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  } as SxProps<Theme>,

  actionButton: {
    ...buttonReset,
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: t.actions.fontSize,
    fontWeight: t.actions.fontWeight,
    borderRadius: t.actions.radius,
    p: t.actions.padding,
    color: t.actions.color,
    border: `1px solid ${t.actions.border}`,
    bgcolor: t.actions.bg,
    "&:hover": { bgcolor: t.actions.hoverBg },
    "&:disabled": { opacity: 0.55, cursor: "default" },
  } as SxProps<Theme>,

  // ------------------------------------------------------------------- viewer

  /** A page-like panel inside the modal, with the quote highlighted on it (the mock's viewer body). */
  viewerPage: {
    bgcolor: t.viewer.pageBg,
    border: `1px solid ${t.viewer.pageBorder}`,
    borderRadius: t.viewer.pageRadius,
    p: t.viewer.pagePadding,
  } as SxProps<Theme>,

  viewerQuote: {
    m: 0,
    bgcolor: t.viewer.quoteBg,
    borderLeft: `3px solid ${t.viewer.quoteRule}`,
    borderRadius: t.viewer.quoteRadius,
    p: t.viewer.quotePadding,
    fontSize: t.viewer.textFontSize,
    lineHeight: t.viewer.textLineHeight,
    color: t.viewer.textColor,
    overflowWrap: "anywhere",
  } as SxProps<Theme>,

  // ----------------------------------------------------------------- composer

  /** The floating composer's band at the bottom of the frame. */
  composerWrap: {
    flexShrink: 0,
    pt: t.chatArea.composerPaddingTop,
    pb: {
      xs: "max(8px, env(safe-area-inset-bottom))",
      md: t.chatArea.composerPaddingBottom,
    },
  } as SxProps<Theme>,

  composer: (focused: boolean) =>
    ({
      display: "flex",
      alignItems: "flex-end",
      gap: t.composer.gap,
      bgcolor: t.composer.bg,
      border: `${t.composer.borderWidth} solid ${
        focused ? t.composer.focusBorder : t.composer.border
      }`,
      borderRadius: t.composer.radius,
      p: t.composer.padding,
      boxShadow: t.composer.shadow,
      transition: "border-color .15s",
    }) as SxProps<Theme>,

  composerInput: {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: "none",
    resize: "none",
    background: "transparent",
    fontFamily: "inherit",
    fontSize: t.composer.fontSize,
    fontWeight: t.composer.fontWeight,
    lineHeight: t.composer.lineHeight,
    color: t.header.titleColor,
    py: t.composer.inputPaddingY,
    "&::placeholder": {
      color: t.composer.noteColor,
      fontWeight: 500,
      opacity: 1,
    },
  } as SxProps<Theme>,

  sendButton: {
    ...buttonReset,
    width: t.composer.sendSize,
    height: t.composer.sendSize,
    borderRadius: t.composer.sendRadius,
    border: 0,
    bgcolor: t.composer.sendBg,
    color: t.composer.sendColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background-color .15s",
    "&:hover": { bgcolor: t.composer.sendHoverBg },
    "&:disabled": {
      bgcolor: t.composer.sendDisabledBg,
      color: t.composer.sendDisabledColor,
      cursor: "not-allowed",
    },
  } as SxProps<Theme>,

  /** Note on the left, keyboard hint on the right, under the box. */
  composerHint: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: t.composer.hintGap,
    mt: t.composer.hintMarginTop,
    px: t.composer.hintPaddingX,
  } as SxProps<Theme>,

  composerNote: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    minWidth: 0,
    fontSize: t.composer.noteFontSize,
    fontWeight: t.composer.noteFontWeight,
    color: t.composer.noteColor,
  } as SxProps<Theme>,

  composerHintText: {
    fontSize: t.composer.keysFontSize,
    fontWeight: t.composer.keysFontWeight,
    color: t.composer.keysColor,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  // -------------------------------------------------------------- dashboard

  dashboardCard: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
  } as SxProps<Theme>,

  dashboardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  } as SxProps<Theme>,

  dashboardTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    minWidth: 0,
  } as SxProps<Theme>,

  dashboardIconTile: {
    width: 36,
    height: 36,
    borderRadius: "11px",
    bgcolor: t.empty.iconTileBg,
    color: t.empty.iconColor,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  dashboardChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 1,
  } as SxProps<Theme>,
};

export default contractSearchStyles;
